// import DateFnsAdapter from '@mui/lab/AdapterDateFns'
import AdapterDayjs from '@mui/lab/AdapterDayjs'
import LocalizationProvider from '@mui/lab/LocalizationProvider'
import CssBaseline from '@mui/material/CssBaseline'
import { ThemeProvider } from '@mui/material/styles'
import { QueryClient, QueryClientProvider, QueryClientConfig } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import dayjs from 'dayjs'
import isBetween from 'dayjs/plugin/isBetween'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import React from 'react'
import { HelmetProvider } from 'react-helmet-async'
import { AppRouter } from './components/Router/Router'
import { SnackbarProvider } from './components/Snackbar'
import { ConfirmProvider } from './contexts/ConfirmContext'
import { AccountLayoutStateProvider } from './hooks/useAccountLayoutState'
import { AuthProvider } from './hooks/useAuth'
import { GlobalContextProvider } from './hooks/useGlobalContext'
import { ReconciliationProvider } from './hooks/useReconciliationContext'
import theme from './utils/theme'

// Enable plugins
dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(isBetween)
dayjs.tz.setDefault('America/Denver')

export const queryClientConfig: QueryClientConfig = {
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      retry: false,
      cacheTime: 0,
    },
  },
}

const queryClient = new QueryClient(queryClientConfig)

export default function App(): JSX.Element {
  const CURRENT_VERSION = __VITE_GIT_HASH__

  React.useEffect(() => {
    const checkVersion = async (): Promise<void> => {
      try {
        const res = await fetch('/meta.json', { cache: 'no-cache' })
        const { commitHash } = await res.json()

        if (commitHash !== CURRENT_VERSION) {
          window.location.reload()
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Version check failed:', err)
      }
    }

    window.addEventListener('focus', checkVersion)

    const interval = setInterval(checkVersion, 15 * 60 * 1000) // 15 minutes

    return () => {
      window.removeEventListener('focus', checkVersion)
      clearInterval(interval)
    }
  }, [CURRENT_VERSION])

  return (
    <React.Suspense fallback="">
      <HelmetProvider>
        <QueryClientProvider client={queryClient}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <ThemeProvider theme={theme}>
              <CssBaseline />
              <AuthProvider>
                <ReconciliationProvider>
                  <AccountLayoutStateProvider>
                    <GlobalContextProvider>
                      <SnackbarProvider>
                        <ConfirmProvider>
                          <AppRouter/>
                        </ConfirmProvider>
                      </SnackbarProvider>
                    </GlobalContextProvider>
                  </AccountLayoutStateProvider>
                </ReconciliationProvider>
              </AuthProvider>
            </ThemeProvider>
          </LocalizationProvider>
          <ReactQueryDevtools initialIsOpen={false} />
        </QueryClientProvider>
      </HelmetProvider>
    </React.Suspense>
  )
}
